
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { UserRole } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  LayoutGrid,
  History as HistoryIcon,
  Database,
  Users,
  Settings,
  LogOut,
  CreditCard,
  Building,
  Box,
  Archive,
  Brain,
  Zap,
  Shield,
} from 'lucide-react';

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // V8.1: Detect Active Project from URL
  const location = window.location.pathname;
  const projectMatch = location.match(/\/projects\/([^/]+)/);
  const activeProjectId = (projectMatch && projectMatch[1] !== 'new') ? projectMatch[1] : null;

  const menuItems = [
    { icon: LayoutGrid, label: 'Dashboard', path: '/dashboard' },
    { icon: Box, label: 'Projects', path: '/projects' },

    // Project Specific Tools (Only show if inside a project)
    ...(activeProjectId ? [
      {
        icon: Archive,
        label: 'Entidades',
        path: `/projects/${activeProjectId}/entities`
      },


      {
        icon: Database,
        label: 'Data Ingest',
        path: `/projects/${activeProjectId}/ingestion`
      },
      {
        icon: HistoryIcon,
        label: 'History Log',
        path: `/projects/${activeProjectId}/history`
      },
      {
        icon: Settings,
        label: 'Configurations',
        path: `/projects/${activeProjectId}/settings`
      }
    ] : []),

    // Global / Admin Tools
    // If inside a project, we might want to hide global dashboard to avoid confusion, 
    // or keep them as "Back to Global"

    // Always show these at bottom
    { path: '/skope', label: 'Skope WCAG', icon: Shield },
    { path: '/performance', label: 'Performance', icon: Zap },
    { path: '/billing', label: 'Billing', icon: CreditCard },
    { path: '/users', label: 'Usuarios', icon: Users, roles: [UserRole.ADMIN, UserRole.QA_LEAD] },
    { path: '/clients', label: 'Clientes', icon: Building, roles: [UserRole.ADMIN] },
    { path: '/admin/ai-gateway', label: 'AI Gateway', icon: Settings, roles: [UserRole.ADMIN] },
    { path: '/admin/llm-assignments', label: 'LLMs', icon: Brain, roles: [UserRole.ADMIN, UserRole.TENANT_ADMIN] },
  ];

  const filteredItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="w-[200px] bg-surface-dark border-r border-slate-800 flex flex-col h-screen">
      {/* Logo */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 overflow-hidden rounded-lg">
            <img src="/favicon.png" alt="FlowSpec QA Logo" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="font-bold text-white text-sm">FlowSpec QA</h1>
            <p className="text-[10px] text-slate-500">Bot Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {filteredItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive
                ? 'bg-primary text-white'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-white">
              {user?.name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.name || 'Admin User'}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {user?.email || 'qa.smartcases.cl'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
